import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  Building2,
  Coins,
  TrendingUp,
  ShieldCheck,
  ArrowUpRight,
  Sparkles,
  CheckCircle2,
  Plane,
  MapPin,
  Palmtree,
  Star,
  Crown,
} from "lucide-react";

const TRIP_IMAGES = {
  goa: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?auto=format&fit=crop&w=1200&q=80",
  kerala: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&w=1200&q=80",
  thailand: "https://images.unsplash.com/photo-1528181304800-259b08848526?auto=format&fit=crop&w=1200&q=80",
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzg0MTA3NTYzfDA&ixlib=rb-4.1.0&q=85";
const CRYPTO_IMG =
  "https://images.unsplash.com/photo-1625014618427-fbc980b974f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNyeXB0byUyMGJsb2NrY2hhaW4lMjBnb2xkfGVufDB8fHx8MTc4NDEwNzU2NHww&ixlib=rb-4.1.0&q=85";
const GOLD_IMG =
  "https://images.unsplash.com/photo-1680448588521-35f5d04a4193?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGNyeXB0byUyMGJsb2NrY2hhaW4lMjBnb2xkfGVufDB8fHx8MTc4NDEwNzU2NHww&ixlib=rb-4.1.0&q=85";
const T1 =
  "https://images.pexels.com/photos/31869537/pexels-photo-31869537.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const T2 =
  "https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg0MTA3NTYzfDA&ixlib=rb-4.1.0&q=85";

const Stat = ({ value, label, testid }) => (
  <div data-testid={testid} className="flex flex-col">
    <div className="font-display text-4xl md:text-5xl text-white tracking-tight">{value}</div>
    <div className="text-xs md:text-sm text-slate-400 uppercase tracking-widest mt-2">{label}</div>
  </div>
);

export default function Landing() {
  const [packages, setPackages] = useState([]);
  const [tripTiers, setTripTiers] = useState([]);
  const [ranks, setRanks] = useState([]);

  useEffect(() => {
    api.get("/packages").then(({ data }) => {
      setPackages(data.packages);
      setTripTiers(data.trip_tiers || []);
      setRanks(data.ranks || []);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#07080a] text-slate-100 noise relative overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-28 md:pt-36 pb-24 gold-glow">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid md:grid-cols-12 gap-12 items-center relative z-10">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold border border-gold/30 rounded-full px-4 py-1.5 mb-8">
              <Sparkles className="w-3.5 h-3.5" /> Real Estate · Crypto Management
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Wealth <span className="text-gold italic">designed.</span><br />
              Futures <span className="text-gold italic">secured.</span>
            </h1>
            <p className="mt-8 text-slate-300 text-lg max-w-2xl leading-relaxed">
              Vision Fashions blends tangible real estate assets with institutional crypto strategies to deliver
              disciplined, high-conviction daily returns for our members.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register">
                <Button data-testid="hero-cta-primary" className="bg-gold text-black hover:bg-[#f3e396] rounded-full px-7 h-12 text-base">
                  Start investing <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#packages">
                <Button data-testid="hero-cta-secondary" variant="outline" className="rounded-full px-7 h-12 text-base bg-transparent border-white/15 text-white hover:bg-white/5">
                  View packages
                </Button>
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-8 max-w-xl">
              <Stat testid="hero-stat-1" value="1%" label="Daily ROI · Premium" />
              <Stat testid="hero-stat-2" value="3×" label="Total Return · Secure" />
              <Stat testid="hero-stat-3" value="200d" label="Fastest cycle" />
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden rounded-md border border-white/10">
              <img
                data-testid="hero-image"
                src={HERO_IMG}
                alt="Luxury real estate"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                <div>
                  <div className="text-xs uppercase tracking-widest text-gold">Portfolio · 24</div>
                  <div className="font-display text-2xl mt-1">Prime Assets Under Vision</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-8 -left-8 w-40 h-40 md:w-48 md:h-48 rounded-md overflow-hidden border border-gold/40 hidden md:block">
              <img src={CRYPTO_IMG} alt="Crypto" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <div className="divider-gold max-w-7xl mx-auto" />

      {/* SERVICES */}
      <section id="services" className="py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-6">
              <div className="text-xs uppercase tracking-[0.3em] text-slate-400 mb-4">01 — What we manage</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight">
                Two disciplines. <br />
                <span className="text-gold italic">One conviction.</span>
              </h2>
            </div>
            <div className="md:col-span-6 text-slate-400 leading-relaxed max-w-xl">
              We do not chase trends. We deploy capital into pillars of durable wealth — prime real estate
              and thoughtfully allocated digital assets — engineered for members who value both stability and upside.
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div data-testid="service-real-estate" className="card-elev card-hover rounded-md overflow-hidden">
              <div className="aspect-[16/9] overflow-hidden">
                <img src={HERO_IMG} alt="Real Estate" className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <Building2 className="w-6 h-6 text-gold mb-4" />
                <div className="font-display text-2xl mb-3">Real Estate Portfolios</div>
                <p className="text-slate-400 leading-relaxed">
                  Prime residential and commercial properties across select markets, curated for long-run
                  appreciation and stabilized yield.
                </p>
              </div>
            </div>
            <div data-testid="service-crypto" className="card-elev card-hover rounded-md overflow-hidden">
              <div className="aspect-[16/9] overflow-hidden">
                <img src={GOLD_IMG} alt="Crypto" className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <Coins className="w-6 h-6 text-gold mb-4" />
                <div className="font-display text-2xl mb-3">Crypto Management</div>
                <p className="text-slate-400 leading-relaxed">
                  Institutional-grade allocation across major digital assets and yield strategies —
                  disciplined, hedged, and transparent.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section id="packages" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-6">
              <div className="text-xs uppercase tracking-[0.3em] text-slate-400 mb-4">02 — Membership tiers</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight">
                Choose your <span className="text-gold italic">arc.</span>
              </h2>
            </div>
            <div className="md:col-span-6 text-slate-400 leading-relaxed max-w-xl">
              Two thoughtfully engineered packages. Both compound daily. Refer partners and unlock a
              higher multiplier plus <span className="text-gold">5% direct commission</span> and up to 25 levels of ROI income.
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Premium */}
            <div data-testid="pkg-premium" className="card-elev card-hover rounded-md p-8 md:p-10 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
              <div className="relative">
                <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Premium</div>
                <div className="font-display text-4xl mb-2">1% Daily · 200 Days</div>
                <div className="text-slate-400">$100 – $10,000</div>

                <div className="mt-10 grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs uppercase text-slate-500 tracking-widest">No referral</div>
                    <div className="font-display text-3xl mt-2">2× total</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-gold tracking-widest">With referral</div>
                    <div className="font-display text-3xl mt-2 text-gold">3× total</div>
                  </div>
                </div>

                <ul className="mt-10 space-y-3 text-slate-300 text-sm">
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-gold" /> Daily accrual, transparent</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-gold" /> Referral commission on top</li>
                  <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-gold" /> Withdraw anytime after approval</li>
                </ul>

                <Link to="/register">
                  <Button data-testid="pkg-premium-cta" className="mt-10 bg-gold text-black hover:bg-[#f3e396] rounded-full px-6 h-11">
                    Start Premium <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Secure */}
            <div data-testid="pkg-secure" className="card-elev card-hover rounded-md p-8 md:p-10 relative overflow-hidden">
              <div className="absolute -top-24 -left-24 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl" />
              <div className="relative">
                <div className="text-xs uppercase tracking-[0.3em] text-cyan-300 mb-4">Secure</div>
                <div className="font-display text-4xl mb-2">0.5% Daily · 400 Days</div>
                <div className="text-slate-400">$100 – $100,000</div>

                <div className="mt-10 grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs uppercase text-slate-500 tracking-widest">Guaranteed cycle</div>
                    <div className="font-display text-3xl mt-2">3× total</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-cyan-300 tracking-widest">Referral</div>
                    <div className="font-display text-3xl mt-2 text-cyan-300">+ commission</div>
                  </div>
                </div>

                <ul className="mt-10 space-y-3 text-slate-300 text-sm">
                  <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-cyan-300" /> Lower risk, longer horizon</li>
                  <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-cyan-300" /> Compounding stability</li>
                  <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-cyan-300" /> Higher ceiling — up to $100k</li>
                </ul>

                <Link to="/register">
                  <Button data-testid="pkg-secure-cta" className="mt-10 bg-white text-black hover:bg-slate-200 rounded-full px-6 h-11">
                    Start Secure <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LAUNCH OFFER — TRIPS */}
      <section id="launch-offer" data-testid="launch-offer-section" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold border border-gold/30 rounded-full px-4 py-1.5 mb-6">
                <Plane className="w-3.5 h-3.5" /> Launch offer · Limited time
              </div>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight">
                Invest more. <span className="text-gold italic">Travel further.</span>
              </h2>
            </div>
            <div className="md:col-span-5 text-slate-400 leading-relaxed">
              As part of our launch, we're gifting all-expenses-paid getaways when your cumulative approved investment crosses these tiers. Highest tier wins.
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {tripTiers.map((t, idx) => (
              <div
                key={t.id}
                data-testid={`trip-tier-${t.id}`}
                className="card-elev card-hover rounded-md overflow-hidden group"
              >
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img
                    src={TRIP_IMAGES[t.id]}
                    alt={t.destination}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2 text-xs uppercase tracking-widest text-gold bg-black/50 backdrop-blur px-3 py-1 rounded-full border border-gold/30">
                    <MapPin className="w-3 h-3" /> Tier {idx + 1}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="font-display text-3xl text-white">{t.name}</div>
                    <div className="text-slate-300 text-sm mt-1">{t.destination}</div>
                  </div>
                </div>
                <div className="p-6 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase text-slate-500 tracking-widest">Unlock at</div>
                    <div className="font-display text-2xl text-gold mt-1">${t.threshold.toLocaleString()}</div>
                  </div>
                  <Palmtree className="w-6 h-6 text-gold/70" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-xs text-slate-500 font-mono-x">
            * Trip rewards are fulfilled after admin verification. Cumulative approved investment across all packages counts toward tier unlocks.
          </div>
        </div>
      </section>

      {/* RANKS */}
      <section id="ranks" data-testid="ranks-section" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold border border-gold/30 rounded-full px-4 py-1.5 mb-6">
                <Crown className="w-3.5 h-3.5" /> VF Rank Program
              </div>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight">
                Build a team. <span className="text-gold italic">Ascend the ranks.</span>
              </h2>
            </div>
            <div className="md:col-span-5 text-slate-400 leading-relaxed">
              As your downline's cumulative investment grows, you climb our 9-star rank ladder. Higher ranks signal your leadership within the Vision Fashions community.
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {ranks.map((r) => (
              <div
                key={r.star}
                data-testid={`landing-rank-${r.star}`}
                className="card-elev card-hover rounded-md p-6 relative overflow-hidden"
              >
                <div className="absolute -top-16 -right-16 w-32 h-32 rounded-full bg-gold/10 blur-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: Math.min(r.star, 5) }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-current text-gold" />
                    ))}
                    {r.star > 5 && <span className="text-gold font-mono-x text-xs ml-1">+{r.star - 5}</span>}
                  </div>
                  <div className="font-display text-xl">{r.name}</div>
                  <div className="text-xs uppercase text-slate-500 tracking-widest mt-4">Team investment</div>
                  <div className="font-mono-x text-gold text-lg mt-1">
                    ${r.threshold >= 1000000 ? (r.threshold / 1000000).toFixed(2).replace(/\.?0+$/, "") + "M" : (r.threshold / 1000) + "K"}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-xs text-slate-500 font-mono-x">
            * Team investment = cumulative approved investment across all 25 levels of your referral network.
          </div>
        </div>
      </section>



      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-slate-400 mb-4">03 — The process</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-16">
            From onboarding to <span className="text-gold italic">daily yield.</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { n: "01", t: "Create account", d: "Sign up in seconds. Optionally enter a referral code to unlock the 3× tier." },
              { n: "02", t: "Choose package", d: "Premium or Secure. Enter your amount within your tier limits." },
              { n: "03", t: "Deposit crypto", d: "Send USDT / BTC / ETH to our platform wallet and paste the tx hash." },
              { n: "04", t: "Earn daily", d: "After admin approval, your ROI accrues daily. Withdraw anytime." },
            ].map((s) => (
              <div key={s.n} data-testid={`step-${s.n}`} className="border-l border-gold/30 pl-6">
                <div className="font-mono-x text-xs text-gold mb-3">{s.n}</div>
                <div className="font-display text-2xl mb-2">{s.t}</div>
                <div className="text-slate-400 text-sm leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-slate-400 mb-4">04 — Voices</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-16 max-w-3xl">
            Trusted by discerning members across two continents.
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div data-testid="testimonial-1" className="card-elev rounded-md p-8 md:p-10 flex gap-6 items-start">
              <img src={T1} className="w-20 h-20 rounded-full object-cover border border-gold/40" alt="Amara" />
              <div>
                <p className="font-display text-2xl leading-snug">
                  "The Premium package paid the referral multiplier as promised. Reporting is clean."
                </p>
                <div className="mt-5 text-sm text-slate-400">Amara N. · Real Estate Partner</div>
              </div>
            </div>
            <div data-testid="testimonial-2" className="card-elev rounded-md p-8 md:p-10 flex gap-6 items-start">
              <img src={T2} className="w-20 h-20 rounded-full object-cover border border-gold/40" alt="Rahul" />
              <div>
                <p className="font-display text-2xl leading-snug">
                  "Secure package for the long horizon, Premium for velocity. A rare balance."
                </p>
                <div className="mt-5 text-sm text-slate-400">Rahul S. · Angel Investor</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5 gold-glow">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 text-center">
          <TrendingUp className="w-8 h-8 text-gold mx-auto mb-8" />
          <h2 className="font-display text-4xl md:text-6xl tracking-tight">
            Your next chapter of <span className="text-gold italic">wealth</span> begins here.
          </h2>
          <p className="mt-8 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Join Vision Fashions today. Refer a partner and unlock the 3× multiplier with 5% direct commission plus 25 levels of ROI income (12% at L1).
          </p>
          <div className="mt-10 flex justify-center gap-4">
            <Link to="/register">
              <Button data-testid="cta-register-btn" className="bg-gold text-black hover:bg-[#f3e396] rounded-full px-8 h-12 text-base">
                Create account <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button data-testid="cta-login-btn" variant="outline" className="rounded-full px-8 h-12 text-base bg-transparent border-white/15 text-white hover:bg-white/5">
                I have an account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
